import { createRouter, createWebHistory } from 'vue-router'
// Static imports for immediate auth needs
import Login from '../views/auth/Login.vue'
import Signup from '../views/auth/Signup.vue'
import Verification from '../views/auth/Verification.vue'
import { user } from '../scripts/utils'
import { states } from '../main_store'
import { watch } from 'vue'
import { updateThemeColor } from '../scripts/themeColor'

import { getDoc, doc } from "firebase/firestore"
import { db } from '@/firebase/config'
import { canManagePoints } from '@/views/admin/checkAdmin'

// Wait for Firebase auth to be ready before making routing decisions
const waitForAuthReady = () => {
  return new Promise((resolve) => {
    // If auth is already ready, resolve immediately
    if (states.value.authIsReady) {
      resolve()
      return
    }
    // Otherwise, watch for authIsReady to become true
    const unwatch = watch(
      () => states.value.authIsReady,
      (isReady) => {
        if (isReady) {
          unwatch()
          resolve()
        }
      },
      { immediate: true }
    )
  })
}

// Lazy-loaded components for code-splitting
const Home = () => import('../views/Home.vue')
const PrivacyPolicy = () => import('@/views/PrivacyPolicy.vue')

// Chat components
const MyChat = () => import('../views/chat/MyChat.vue')
const ChatControl = () => import('../views/chatControl/MyChat.vue')

// Profile components
const MyProfile = () => import('../views/profile/MyProfile.vue')
const MyFamily = () => import('../views/profile/Family.vue')
const EditProfile = () => import('../views/profile/EditProfile.vue')
const InfoForVols = () => import('../views/profile/InfoForVols.vue')
const ContactUs = () => import('../views/profile/ContactUs.vue')

// Request and volunteering components
const MyRequests = () => import('../views/my_requests/MyRequests.vue')
const MyVolunteering = () => import('../views/my_volunteering/MyVolunteering.vue')
const Feedback = () => import('../views/my_volunteering/Feedback.vue')
const Volunteer = () => import('../views/volunteer/Volunteer.vue')
const RequestVolunteer = () => import('../views/request_volunteer/RequestVolunteer.vue')
const DatePicker = () => import('../views/request_volunteer/DatePicker.vue')
const RequestReceived = () => import('../views/request_volunteer/RequestReceived.vue')

// Admin components
const UsersManager = () => import('../views/admin/UsersManager.vue')
const RequestManager = () => import('../views/admin/RequestManager.vue')
const ManageBackground = () => import('../views/admin/ManageBackground.vue')
const FeedbackManager = () => import('../views/admin/FeedbackManager.vue')
const PicAuth = () => import('../views/admin/PicAuth.vue')
const CoordinatorsManager = () => import('../views/admin/CoordinatorsManager.vue')
const VideoManager = () => import('../views/admin/VideoManager.vue')
const PointsManager = () => import('../views/admin/PointsManager.vue')

const requireAuth = async (to, from, next) => {
  await waitForAuthReady()
  if (!user.value) {
    next({ name: 'Login' })
  } else {
    next()
  }
}

const requireAdminAuth = async (to, from, next) => {
  await waitForAuthReady()
  if (!user.value) {
    next({ name: 'Login' })
  } else {
    const userDocRef = doc(db, "users", user.value.uid);
    const userDocSnap = await getDoc(userDocRef);
    if (userDocSnap.data().isAdmin) {
      next()
    } else {
      next({ name: 'Home' })
    }
  }
}

const requireCoordinatorAuth = async (to, from, next) => {
  await waitForAuthReady()
  if (!user.value) {
    next({ name: 'Login' })
  } else {
    const userDocRef = doc(db, "users", user.value.uid);
    const userDocSnap = await getDoc(userDocRef);
    const userData = userDocSnap.data();
    if (userData.isCoordinator || userData.isAdmin) {
      next()
    } else {
      next({ name: 'Home' })
    }
  }
}

const requirePointsManagerAuth = async (to, from, next) => {
  await waitForAuthReady()
  if (!user.value) {
    next({ name: 'Login' })
  } else {
    const userDocRef = doc(db, "users", user.value.uid);
    const userDocSnap = await getDoc(userDocRef);
    if (canManagePoints(userDocSnap.data())) {
      next()
    } else {
      next({ name: 'Home' })
    }
  }
}

const requireNoAuth = async (to, from, next) => {
  await waitForAuthReady()
  if (user.value) {
    next({ name: 'Home' })
  } else {
    next()
  }
}

const routes = [
  {
    path: '/',
    name: 'Home',
    component: Home,
    beforeEnter: requireAuth
  },
  {
    path: '/login',
    name: 'Login',
    component: Login,
    beforeEnter: requireNoAuth
  },
  {
    path: '/signup',
    name: 'Signup',
    component: Signup,
    beforeEnter: requireNoAuth
  },
  {
    path: '/verification',
    name: 'Verification',
    component: Verification, // Replace with your actual component
    props: true,
    beforeEnter: requireNoAuth
  },
  {
    path: '/mychat',
    name: 'MyChat',
    component: MyChat,
    beforeEnter: requireAuth
  },
  {
    path: '/chatcontrol',
    name: 'ChatControl',
    component: ChatControl,
    beforeEnter: requireAuth
  },
  {
    path: '/myprofile',
    name: 'MyProfile',
    component: MyProfile,
    beforeEnter: requireAuth
  },
  {
    path: '/myrequests',
    name: 'MyRequests',
    component: MyRequests,
    beforeEnter: requireAuth
  },
  {
    path: '/myfamily',
    name: 'MyFamily',
    component: MyFamily,
    beforeEnter: requireAuth
  },
  {
    path: '/myvolunteering',
    name: 'MyVolunteering',
    component: MyVolunteering,
    beforeEnter: requireAuth
  },
  {
    path: '/volunteer',
    name: 'Volunteer',
    component: Volunteer,
    beforeEnter: requireAuth
  },
  {
    path: '/requestvolunteer',
    name: 'RequestVolunteer',
    component: RequestVolunteer,
    beforeEnter: requireAuth
  },
  {
    path: '/feedback',
    name: 'Feedback',
    component: Feedback,
    beforeEnter: requireAuth
  },
  {
    path: '/request_received',
    name: 'RequestReceived',
    component: RequestReceived,
    beforeEnter: requireAuth
  },
  {
    path: '/editprofile',
    name: 'EditProfile',
    component: EditProfile,
    beforeEnter: requireAuth
  },
  {
    path: '/info_for_volunteers',
    name: 'InfoForVols',
    component: InfoForVols,
    beforeEnter: requireAuth
  },
  {
    path: '/contact_us',
    name: 'ContactUs',
    component: ContactUs,
    beforeEnter: requireAuth
  },
  {
    path: '/date_picker',
    name: 'DatePicker',
    component: DatePicker,
    beforeEnter: requireAuth
  },
  {
    path: '/admin/users_manager',
    name: 'UsersManager',
    component: UsersManager,
    beforeEnter: requireAdminAuth
  },
  {
    path: '/admin/request_manager',
    name: 'RequestManager',
    component: RequestManager,
    beforeEnter: requireAdminAuth
  },
  {
    path: '/admin/background_manager',
    name: 'ManageBackground',
    component: ManageBackground,
    beforeEnter: requireAdminAuth
  },
  {
    path: '/admin/feedback_manager',
    name: 'FeedbackManager',
    component: FeedbackManager,
    beforeEnter: requireAdminAuth
  },
  {
    path: '/admin/pic_auth',
    name: 'PicAuth',
    component: PicAuth,
    beforeEnter: requireAdminAuth
  },
  {
    path: '/admin/coordinators_manager',
    name: 'CoordinatorsManager',
    component: CoordinatorsManager,
    beforeEnter: requireCoordinatorAuth
  },
  {
    path: '/admin/video_manager',
    name: 'VideoManager',
    component: VideoManager,
    beforeEnter: requireAdminAuth
  },
  {
    path: '/admin/points_manager',
    name: 'PointsManager',
    component: PointsManager,
    beforeEnter: requirePointsManagerAuth
  },
  {
    path: '/privacy-policy',
    name: 'PrivacyPolicy',
    component: PrivacyPolicy,
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/'
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

// Update mobile status bar color based on route
router.afterEach((to) => {
  updateThemeColor(to.name)
})

export default router
